import { Alignments } from 'rich-text-lib/constants/alignments';
import { TableAlignmentClasses, CELL_VERTICAL_ALIGNMENTS, TABLE_SETTINGS, hstableConfig, TableAlignmentFormats, CELL_HORIZONTAL_ALIGNMENTS, TableAlignmentStyles } from '../constants/hstable2';
import { getPluginSetting } from '../utils';
export const isTableNode = node => typeof node === 'object' && node.nodeName === 'TABLE';
export const asPercentage = (numerator, denominator) => {
  return Math.round(numerator / denominator * 100);
};

// Returns the selected table node or null
export function getSelectedTableNode(editor, node) {
  const selectedNode = node || editor.selection.getNode();
  if (!selectedNode) return null;
  const body = editor.getBody();
  const tableNode = editor.dom.getParent(selectedNode, parent => body.contains(parent) && isTableNode(parent));
  return tableNode;
}
export function getSelectedCells(editor, node) {
  const selectedCells = editor.dom.select('td[data-mce-selected],th[data-mce-selected]');
  if (selectedCells.length) {
    return selectedCells;
  }
  const selectedNode = node || editor.selection.getNode();
  if (!selectedNode) {
    return [];
  }
  if (selectedNode.nodeName === 'TD' || selectedNode.nodeName === 'TH') {
    return [selectedNode];
  }

  // TODO Fix case where selection spans range of cells but tinymce hasn't applied data-mce-selected, selectedNode can be <tr>
  const parentCell = editor.dom.getParent(selectedNode, 'td,th');
  return parentCell ? [parentCell] : [];
}
export function getCommonValue(els, getter, defaultValue) {
  if (!els || !els.length) {
    return undefined;
  }
  const commonValue = getter(els[0]);
  for (let i = 0; i < els.length; i++) {
    const elValue = getter(els[i]);
    if (commonValue !== elValue) {
      return defaultValue;
    }
  }
  return commonValue;
}
export function getVTextAlign(editor, node) {
  let valign = CELL_VERTICAL_ALIGNMENTS.find(name => editor.formatter.matchNode(node, `valign${name}`));
  if (!valign) {
    const computedVAlign = getComputedStyle(node).verticalAlign;
    if (CELL_VERTICAL_ALIGNMENTS.includes(computedVAlign)) {
      valign = computedVAlign;
    }
  }
  return valign;
}
export function getTextAlign(editor, node) {
  let align = CELL_HORIZONTAL_ALIGNMENTS.find(name => editor.formatter.matchNode(node, `align${name}`));
  if (!align) {
    const computedStyles = getComputedStyle(node);
    const isLTR = computedStyles.direction === 'ltr';
    let computedAlign = computedStyles.textAlign;
    if (computedAlign === 'start') {
      computedAlign = isLTR ? 'left' : 'right';
    } else if (computedAlign === 'end') {
      computedAlign = isLTR ? 'right' : 'left';
    }
    if (CELL_HORIZONTAL_ALIGNMENTS.includes(computedAlign)) {
      align = computedAlign;
    }
  }
  return align;
}
export function getStyle(node, name, defaultValue = '') {
  return node && node.style ? node.style.getPropertyValue(name) || defaultValue : '';
}
export function getDimension(node, dimension) {
  const style = getStyle(node, dimension);
  return style || getComputedStyle(node).getPropertyValue(dimension);
}
export function isResponsiveTableNode(tableNode) {
  const wrapper = tableNode.parentElement;
  return wrapper && wrapper.tagName === 'DIV' && wrapper.dataset.hsResponsiveTable === 'true' && getStyle(wrapper, 'overflow-x') === 'auto';
}
export function getLayout(editor, node) {
  const {
    [hstableConfig.USE_ALIGNMENT_CLASSES]: useAlignmentClasses
  } = getPluginSetting(editor, TABLE_SETTINGS, {});
  let align = '';
  const isResponsive = isResponsiveTableNode(node);
  const targetNode = isResponsive ? node.parentNode : node;
  if (useAlignmentClasses) {
    align = Object.values(TableAlignmentClasses).find(alignmentClass => editor.dom.hasClass(targetNode, alignmentClass)) || TableAlignmentClasses[Alignments.NONE];
  } else {
    let formatMatch;
    if (isResponsive) {
      if (['left', 'right'].includes(getStyle(targetNode, 'float'))) {
        formatMatch = `align${getStyle(targetNode, 'float')}`;
      } else if (getStyle(targetNode, 'margin-left') === 'auto' && getStyle(targetNode, 'margin-right') === 'auto') {
        formatMatch = 'aligncenter';
      } else {
        formatMatch = 'alignfull';
      }
    } else {
      formatMatch = ['alignleft', 'aligncenter', 'alignright'].find(format => editor.formatter.matchNode(targetNode, format)) || 'alignfull';
    }
    align = TableAlignmentFormats[formatMatch];
  }
  return align;
}
function getDistantParent(node, nodeName) {
  let parent = node.parentNode;
  while (parent && parent.nodeName !== nodeName) {
    parent = parent.parentNode;
  }
  return parent;
}
export function getParentTableFromNode(node) {
  return getDistantParent(node, 'TABLE');
}
export function getParentRowFromNode(node) {
  return getDistantParent(node, 'TR');
}
const SPANNED = 'SPANNED';
const HOLE = 'HOLE';
// Build a two-dimensional array which maps out the table.
// The maps contains table cell elements at their correct index
// accounting for colspan/rowspan, leaving markers to indicate
// indexes which are spanned by another cell
function buildTableMap(table) {
  // Get all <tr> elements which are children of the table
  // (excluding <tr> elements in nested tables)
  const trs = [...table.querySelectorAll('tr')].filter(tr => getParentTableFromNode(tr) === table);

  // Get an array of <td> or <th> elements from each <tr>
  // (excluding elements from nested <tr>s)
  const rowsOfCells = trs.map(tr => [...tr.querySelectorAll('td,th')].filter(el => getParentRowFromNode(el) === tr));
  const tableMap = [];
  rowsOfCells.forEach((cellElements, rowIdx) => {
    // Keep track of the column index for the current cell element
    let colIdx = 0;
    let rowMap = tableMap[rowIdx];

    // The row may not exist in the tableMap yet if no previously examined
    // cells have spanned into it from above.
    if (!Array.isArray(rowMap)) {
      rowMap = tableMap[rowIdx] = [];
    }

    // Fast forward to first empty slot in the row map
    // Skips over SPANNED cells which previously examined cells are occupying due to rowspan
    while (colIdx < rowMap.length && rowMap[colIdx] !== HOLE) {
      colIdx++;
    }

    // For each cell element in this <tr>
    cellElements.forEach(cellElement => {
      const colspan = cellElement.getAttribute('colspan') ? parseInt(cellElement.getAttribute('colspan'), 10) : 1;
      const rowspan = cellElement.getAttribute('rowspan') ? parseInt(cellElement.getAttribute('rowspan'), 10) : 1;

      // We will be adding a block of entries of size `rowspan * colspan` to the map
      // For each row in this block...
      for (let i = 0; i < rowspan; i++) {
        const rowOffset = rowIdx + i;
        // Get the corresponding row from the cell map
        let spannedRow = tableMap[rowOffset];
        // It might not exist yet if we are spanning into a new row.
        if (!Array.isArray(spannedRow)) {
          spannedRow = tableMap[rowOffset] = [];
        }

        // For each column in the block...
        for (let j = 0; j < colspan; j++) {
          // colIdx should point to the starting index for this cell
          // adding the offset j to account for colspan
          let colOffset = colIdx + j;

          // The first item inserted in the map is the cell itself
          // Otherwise we are inserting SPANNED markers to show that a cell
          // occupies these spots in the map via colspan or rowspan
          const isCellOrigin = i === 0 && j === 0;
          const cellContentToInsert = isCellOrigin ? cellElement : SPANNED;

          // Again we need to skip to the next available slot in the map
          // As cells from previous rows may have spanned into the current row
          // Effectively pushing cells in that row further along
          while (colOffset < spannedRow.length && spannedRow[colOffset] !== HOLE) {
            colOffset++;
          }

          // If the target column offset is beyond the length of the current row map,
          // then we are spanning into a row beyond where we have traversed so far.
          // We need to leave behind HOLE markers where that row's cells will go.
          while (colOffset > spannedRow.length) {
            spannedRow.push(HOLE);
          }

          // If the row map currently contains a HOLE marker at the target column offset,
          // we replace the HOLE with our cell content.
          // Otherwise, we push() to add a new element to the row map.
          if (spannedRow[colOffset] === HOLE) {
            spannedRow[colOffset] = cellContentToInsert;
          } else {
            spannedRow.push(cellContentToInsert);
          }
        }
      }
      colIdx += colspan;
    });
  });
  return tableMap;
}
function getCellLookupFromTableMap(tableMap) {
  const lookup = new Map();
  for (let rowIdx = 0; rowIdx < tableMap.length; rowIdx++) {
    const row = tableMap[rowIdx];
    for (let colIdx = 0; colIdx < row.length; colIdx++) {
      const entry = row[colIdx];
      if (entry && (entry.nodeName === 'TD' || entry.nodeName === 'TH')) {
        lookup.set(entry, [rowIdx, colIdx]);
      }
    }
  }
  return lookup;
}
export function getColumnsAndRowsFromCells(cells) {
  const tableData = new Map();
  const columns = [];
  const rows = [];
  cells.forEach(cell => {
    const table = getParentTableFromNode(cell);
    if (!tableData.has(table)) {
      const tableMap = buildTableMap(table);
      const cellLookup = getCellLookupFromTableMap(tableMap);
      tableData.set(table, {
        cellLookup,
        tableColumns: {},
        tableRows: {}
      });
    }
    const {
      cellLookup,
      tableColumns,
      tableRows
    } = tableData.get(table);
    const [rowIdx, colIdx] = cellLookup.get(cell);
    if (!tableRows[rowIdx]) {
      const row = [...cellLookup].filter(([__, indices]) => {
        return indices[0] === rowIdx;
      }).map(([c]) => c);
      tableRows[rowIdx] = row;
      rows.push(row);
    }
    if (!tableColumns[colIdx]) {
      const col = [...cellLookup].filter(([__, indices]) => {
        return indices[1] === colIdx;
      }).map(([c]) => c);
      tableColumns[colIdx] = col;
      columns.push(col);
    }
  });
  return {
    columns,
    rows
  };
}
function getLayoutAttributes(node) {
  const tableWidth = getStyle(node, 'width');
  const width = isPercentage(tableWidth) ? tableWidth : `${node.offsetWidth}px`;
  return {
    width,
    float: getStyle(node, 'float'),
    'margin-left': getStyle(node, 'margin-left'),
    'margin-right': getStyle(node, 'margin-right')
  };
}
function getMarginStyles(node) {
  return {
    'margin-top': getStyle(node, 'margin-top'),
    'margin-bottom': getStyle(node, 'margin-bottom'),
    'margin-left': getStyle(node, 'margin-left'),
    'margin-right': getStyle(node, 'margin-right')
  };
}
export function isPercentage(value) {
  return typeof value === 'string' && value.length > 1 && value[value.length - 1] === '%';
}

// Transfer alignment classname from the source node to the target node
function transferAlignmentClassName(editor, sourceNode, targetNode) {
  const currentAlignmentClass = Object.values(TableAlignmentClasses).find(alignmentClass => editor.dom.hasClass(sourceNode, alignmentClass)) || TableAlignmentClasses[Alignments.NONE];
  targetNode.classList.add(currentAlignmentClass);
  Object.values(TableAlignmentClasses).forEach(alignmentClass => {
    editor.dom.removeClass(sourceNode, alignmentClass);
  });
}

// Wraps the table in a responsive div for horizontal scrolling support
export function wrapTableInResponsiveContainer(editor, tableNode) {
  if (isResponsiveTableNode(tableNode)) return;
  const {
    [hstableConfig.USE_ALIGNMENT_CLASSES]: useAlignmentClasses
  } = getPluginSetting(editor, TABLE_SETTINGS, {});
  const useTableContextualToolbar = editor.getParam('useTableContextualToolbar');
  editor.undoManager.transact(() => {
    const tableWrapper = document.createElement('div');
    tableWrapper.dataset.hsResponsiveTable = 'true';
    const tableWidth = getStyle(tableNode, 'width');
    let tableWrapperStyles = {
      'overflow-x': 'auto',
      'max-width': '100%',
      width: tableWidth
    };
    if (useTableContextualToolbar) {
      tableWrapperStyles = Object.assign({}, tableWrapperStyles, getMarginStyles(tableNode));
    }
    if (!useAlignmentClasses) {
      tableWrapperStyles = Object.assign({}, tableWrapperStyles, getLayoutAttributes(tableNode));
    }
    editor.dom.setStyles(tableWrapper, tableWrapperStyles);
    const updatedTableStyles = Object.assign({}, TableAlignmentStyles[Alignments.NONE]);
    if (isPercentage(tableWidth)) {
      updatedTableStyles.width = '100%';
    }
    if (useTableContextualToolbar) {
      updatedTableStyles['margin-top'] = null;
      updatedTableStyles['margin-bottom'] = null;
    }
    editor.dom.setStyles(tableNode, updatedTableStyles);
    if (useAlignmentClasses) {
      transferAlignmentClassName(editor, tableNode, tableWrapper);
    }
    editor.dom.insertAfter(tableWrapper, tableNode);
    editor.dom.remove(tableNode);
    tableWrapper.appendChild(tableNode);
    editor.selection.select(tableNode);
  });
}
export function extractTableFromResponsiveContainer(editor, tableNode) {
  if (!isResponsiveTableNode(tableNode)) return;
  const {
    [hstableConfig.USE_ALIGNMENT_CLASSES]: useAlignmentClasses
  } = getPluginSetting(editor, TABLE_SETTINGS, {});
  const useTableContextualToolbar = editor.getParam('useTableContextualToolbar');
  editor.undoManager.transact(() => {
    const tableWrapper = tableNode.parentElement;
    const tableClone = tableNode.cloneNode(true);
    if (useAlignmentClasses) {
      transferAlignmentClassName(editor, tableWrapper, tableClone);
    }
    const wrapperStyles = Object.assign({}, getLayoutAttributes(tableWrapper), useTableContextualToolbar ? getMarginStyles(tableWrapper) : {});
    editor.dom.setStyles(tableClone, wrapperStyles);
    editor.dom.insertAfter(tableClone, tableWrapper);
    editor.dom.remove(tableWrapper);
    editor.selection.select(tableClone);
  });
}